import Vue from 'vue';
import { isValidArray } from '@razlet/utils';

const MEASUREMENT_UNITS = {
  INCHES: 'inch',
  CENTIMETERS: 'cm',
};

const INCHES_IN_CENTIMETER = 0.393701;

const EMPTY_VALUE_PLUG = '[a]';

export default class CapacityTrunkDimensions {
  #localeKey = 'capacity.info.trunkDimensions';

  #trunkDimensions = [];

  measurementUnit = MEASUREMENT_UNITS.CENTIMETERS;

  constructor (data) {
    if (!isValidArray(data)) return;

    this.#trunkDimensions = data;
  }

  get tablesData () {
    return this.#trunkDimensions.map((trunkDimension, index) => ({
      headers: this.#getTrunkDimensionTableHeaders(trunkDimension, index),
      list: this.#getTrunkDimensionRows(trunkDimension, index),
    }));
  }

  #getTrunkDimensionTableHeaders (trunkDimension, trunkDimensionIndex) {
    const valuesHeaders = this.#getWidthHeaders(trunkDimension, trunkDimensionIndex);
    const resultHeaders = [
      [
        {
          name: this.#localizedHeightHeader,
          rowspan: 3,
        },
        {
          name: this.#localizedWidthHeader,
          colspan: valuesHeaders.length,
          align: 'center',
        },
      ],
      [...valuesHeaders],
      [
        {
          name: this.#localizedLengthHeader,
          colspan: valuesHeaders.length,
          align: 'center',
        },
      ],
    ];

    return resultHeaders;
  }

  #getWidthHeaders (trunkDimension, trunkDimensionIndex) {
    const firstRowKey = this.#trunkDimensionsRowsKeys[trunkDimensionIndex][0];
    const headers = Object.keys(trunkDimension[firstRowKey]);

    return this.#formatValuesByMeasurementUnit(headers)
      .map(header => ({
        name: header,
        width: '60px',
        align: 'center',
      }));
  }

  #getTrunkDimensionRows (trunkDimension, trunkDimensionIndex) {
    return this.#trunkDimensionsRowsKeys[trunkDimensionIndex].map(rowKey => {
      const rowValues = Object.values(trunkDimension[rowKey]);
      rowValues.unshift(rowKey); // inserts height value at the start of values list

      return this.#formatValuesByMeasurementUnit(rowValues);
    });
  }

  #formatValuesByMeasurementUnit (values) {
    return values.map(value => {
      if (value === '') return EMPTY_VALUE_PLUG;

      return this.#isInchesMeasurement
        ? Math.round(+value * INCHES_IN_CENTIMETER)
        : +value;
    });
  }

  get #isInchesMeasurement () {
    return this.measurementUnit === MEASUREMENT_UNITS.INCHES;
  }

  get #trunkDimensionsRowsKeys () {
    return this.#trunkDimensions.map(trunkDimension => Object.keys(trunkDimension));
  }

  get #localizedMeasurementUnit () {
    return Vue.prototype.$localize.t(`base.${this.measurementUnit}`);
  }

  get #localizedHeightHeader () {
    return Vue.prototype.$localize.t(
      `${this.#localeKey}.headers.height`,
      { unit: this.#localizedMeasurementUnit }
    );
  }

  get #localizedWidthHeader () {
    return Vue.prototype.$localize.t(
      `${this.#localeKey}.headers.width`,
      { unit: this.#localizedMeasurementUnit }
    );
  }

  get #localizedLengthHeader () {
    return Vue.prototype.$localize.t(
      `${this.#localeKey}.headers.length`,
      { unit: this.#localizedMeasurementUnit }
    );
  }
}
